import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CourierFileDetailApiAdapter } from '../adapters';
import { CourierFilePaymentFilters } from '../models';

interface UseCourierFilePaymentsProps {
    fileId: number;
    enabled?: boolean;
}

// Mapeo de campos de AG-Grid a campos de API
const FIELD_TO_API_SORT: Record<string, string> = {
    'original_amount': 'original_amount',
    'effective_amount': 'effective_amount',
    'paid_at': 'paid_at',
};

export const useCourierFilePayments = ({ fileId, enabled = true }: UseCourierFilePaymentsProps) => {
    const httpClient = createHttpClient(apiClientWH);
    const adapter = new CourierFileDetailApiAdapter(httpClient);

    const [filters, setFilters] = useState<CourierFilePaymentFilters>({
        page: 1,
        per_page: 15,
    });

    const [sorting, setSorting] = useState<{ field: string; direction: 'asc' | 'desc' } | null>(null);

    // Convertir sorting de AG-Grid a filtros de API
    const apiFilters = useMemo(() => {
        const apiField = sorting?.field
            ? FIELD_TO_API_SORT[sorting.field] || sorting.field
            : undefined;
        const apiDirection = sorting?.direction;

        return {
            ...filters,
            ...(apiField ? { sort_by: apiField } : {}),
            ...(apiDirection ? { sort_direction: apiDirection } : {}),
        };
    }, [filters, sorting]);

    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery({
        queryKey: ['courier-cod-file-payments', fileId, apiFilters],
        queryFn: () => adapter.getCourierFilePayments(fileId, apiFilters),
        enabled: enabled && !!fileId,
        staleTime: 2 * 60 * 1000, // 2 minutos
        gcTime: 5 * 60 * 1000, // 5 minutos
        placeholderData: (previousData) => previousData,
    });

    const updateFilters = (newFilters: Partial<CourierFilePaymentFilters>) => {
        setFilters((prev) => ({
            ...prev,
            ...newFilters,
            page: newFilters.page !== undefined ? newFilters.page : 1,
        }));
    };

    const changePage = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const handleSortingChange = (newSorting: { field: string; direction: 'asc' | 'desc' } | null) => {
        setSorting(newSorting);
        setFilters((prev) => {
            const { sort_by: _, sort_direction: __, ...rest } = prev;
            return { ...rest, page: 1 };
        });
    };

    return {
        payments: data?.data ?? [],
        meta: data?.meta,
        links: data?.links,
        isLoading,
        isFetching,
        error: error ? (error as Error).message : null,
        refetch,
        updateFilters,
        changePage,
        handleSortingChange,
        filters: apiFilters,
        sorting,
    };
};

